<template>
  <div class="auth-page">
    <header class="auth-topbar">
      <img class="mib-top-left-corner" src="../assets/icons/logo-mib.svg" alt="Mailinblack"/>

      <div class="auth-demo">
        <span class="auth-demo-text">{{ $t('authentication.noAccount') }}</span>
        <button class="btn btn-black-outlined" v-on:click="requestDemo()">
          {{ $t('authentication.requestDemo') }}
        </button>
      </div>
    </header>

    <div class="auth-layout">
      <main class="auth-content">
        <Language class="auth-language"></Language>
        <slot></slot>
      </main>

      <aside class="auth-side-card">
        <MibAd></MibAd>
      </aside>
    </div>
  </div>
</template>

<script>
  import Language from './Language.vue'
  import MibAd from './MibAd.vue'

  const REQUEST_DEMO_URL = 'https://www.mailinblack.com/en/demonstration/'
  const REQUEST_DEMO_URL_FR = 'https://www.mailinblack.com/demo/'

  export default {
    name: 'AuthLayout',
    components: {Language, MibAd},
    methods: {
      requestDemo() {
        const url = this.$i18n.locale() === 'FR' ? REQUEST_DEMO_URL_FR : REQUEST_DEMO_URL
        window.open(url, '_blank', 'noopener,noreferrer')
      },
    },
  }
</script>

<style lang="scss" scoped>
  /* =========================
     Auth Page
     ========================= */

  .auth-page {
    min-height: 100vh;
    width: 100%;
    box-sizing: border-box;
    background: #FFFDF5;

    display: flex;
    flex-direction: column;
    align-items: stretch;

    position: relative;
    padding: 2rem 4.5rem 4.5rem;
    overflow-x: clip;
  }

  /* =========================
     Language selector
     ========================= */

  .auth-language {
    position: absolute;
    top: 1rem;
    right: 1.5rem;
    z-index: 20;
  }

  /* =========================
     Logo
     ========================= */

  .auth-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 2rem;
  }

  .mib-top-left-corner {
    display: block;
    width: 11.25rem;
    max-width: 100%;
    height: auto;
    flex-shrink: 0;
  }

  /* =========================
     Request demo
     ========================= */

  .auth-demo {
    display: flex;
    margin-left: auto;
    align-items: center;
    gap: 1rem;
  }

  /* Taille L (48px) sur desktop, M et S dans les media queries plus bas */
  .auth-demo .btn-black-outlined {
    min-height: 3rem;
    padding: 0 1.5rem;
    white-space: nowrap;
  }

  .auth-demo-text {
    /* Poppins (texte) et la police du bouton n'ont pas la même ligne de base : on compense d'environ 1px */
    padding-top: 0.125rem;
    color: #3B3B3B;
    font-family: Poppins, sans-serif;
    font-size: 0.875rem;
    line-height: 1.45;
    white-space: nowrap;
  }

  /* =========================
     Main layout
     ========================= */

  .auth-layout {
    display: grid;
    grid-template-columns:
      minmax(26.25rem, 420fr)
      minmax(30rem, 860fr);

    column-gap: clamp(2rem, 4vw, 5rem);
    row-gap: 1rem;

    width: 100%;
    min-width: 0;
    align-items: stretch;
  }

  /* =========================
     Authentication content
     ========================= */

  .auth-content {
    grid-column: 1;
    grid-row: 1;

    position: relative;

    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;

    padding: clamp(1.5rem, 2.5vw, 2.5rem);
    padding-top: 4rem; /* laisse la place au sélecteur de langue */

    display: flex;
    flex-direction: column;
    justify-content: center;

    background:
      linear-gradient(#ECEADF, #ECEADF) padding-box,
      linear-gradient(135deg, #FF8A3D, #E85D75, #8B5CF6) border-box;

    border: 1px solid transparent;
    border-radius: 1rem;
  }

  /* =========================
     Side card
     ========================= */

  .auth-side-card {
    grid-column: 2;
    grid-row: 1;

    width: 100%;
    min-width: 0;
    box-sizing: border-box;

    border-radius: 1rem;
    overflow: hidden;

    aspect-ratio: 520 / 340;
    position: relative;
  }

  /* =========================
     Tablet and mobile
     ========================= */

  @media (max-width: 55rem) {
    .auth-page {
      padding: 1.5rem 2rem 2rem;
    }

    .auth-topbar {
      margin-bottom: 1.5rem;
    }

    .mib-top-left-corner {
      width: 9rem;
    }

    /* Request demo : taille M */
    .auth-demo .btn-black-outlined {
      min-height: 2.5rem;
      padding: 0 1.25rem;
    }

    .auth-demo-text {
      font-size: 0.8125rem;
    }

    .auth-layout {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;
    }

    .auth-content {
      order: 1;
      width: min(100%, 26.25rem);
      min-width: 20rem;
      padding: 4rem 2rem 2rem;
    }

    .auth-side-card {
      order: 3;
      width: min(100%, 26.25rem);
      margin-top: 0;
    }
  }

  /* =========================
     Small mobile
     ========================= */

  @media (max-width: 30rem) {
    .auth-page {
      /* 20rem (carte) + 2 x 1rem de padding : en dessous, on scrolle au lieu d'écraser la carte */
      min-width: 22rem;
      padding: 1rem;
    }

    .auth-topbar {
      margin-bottom: 1rem;
    }

    .mib-top-left-corner {
      width: 8rem;
    }

    /* Request demo : taille S */
    .auth-demo .btn-black-outlined {
      min-height: 2rem;
      padding: 0 1rem;
      font-size: 0.75rem;
    }

    .auth-content {
      padding: 4rem 1.25rem 2rem;
    }
  }

  /* =========================
     Request demo : texte masqué sous 576px
     ========================= */

  @media (max-width: 36rem) {
    .auth-demo-text {
      display: none;
    }
  }
</style>
